import React, { useMemo, useState, useRef, useEffect, useContext } from "react";
import { mdiCursorMove } from "@mdi/js";
import styled from "styled-components";
import intl from "react-intl-universal";

import lod2 from "../../../../../../script/lod2";
import { Plot3d } from "../../../../../../script/plot3d";
import calcAreaUtils from "../../../../../../script/utils/calcAreaUtils";

import geoRest from "../../../../../../rest/geoRest";
import planRest from "../../../../../../rest/planRest";
import antennaRest from "../../../../../../rest/antennaRest";
import overviewRest from "../../../../../../rest/overviewRest";

import { useOverview3dDisplaySettings } from "./useOverview3dDisplaySettings";
import { useOverview3dBuildingMovement } from "./useOverview3dBuildingMovement";
import { useOverview3dDisplayDataPreprocessing } from "./useOverview3dDisplayDataPreprocessing";

import { Line } from "../../../../../components/Line";
import { Grid } from "../../../../../components/Grid";
import { Spacer } from "../../../../../components/Spacer";
import { Button } from "../../../../../components/Button";
import { Overview3dAreaItem } from "./Overview3dAreaItem";
import { Checkbox } from "../../../../../components/Checkbox";
import { useOnChange } from "../../../../../hooks/useOnChange";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { ScrollBox } from "../../../../../components/ScrollBox";
import { Overview3dAntennasList } from "./Overview3dAntennasList";
import { InputLatLng } from "../../../../../components/InputLatLng";
import { InputNumber } from "../../../../../components/InputNumber";
import { EntryContext } from "../../../../../contexts/EntryContext";

const LayerStyle = styled.div`
    display: ${props => (props.$show ? "flex" : "none")};
`;

const MovementButtonStyle = styled.div`
    position: relative;
`;

const ActiveDotStyle = styled.div`
    position: absolute;
    width: 4px;
    height: 4px;
    background-color: ${props => (props.$active ? props.theme.greenLight : props.theme.black)};
    border-radius: 50%;
    bottom: 2px;
    right: 2px;
    z-index: 10;
    box-shadow: 0 4px 8px rgba(0,0,0,0.3);
`;

export const OverviewLayer3d = props => {
    const { show } = props;

    const buildingColor = "#d4f541";
    const layerName = "overview3d";

    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;
    const { calcSetting, overviewSetting } = entry;
    const { calcAreas } = calcSetting;

    const [plot3d, setPlot3d] = useState();
    const [fieldArray, setFieldArray] = useState();
    const [resultLoaded, setResultLoaded] = useState(true);
    const [areaImportRunning, setAreaImportRunning] = useState(false);

    const resultObj = useRef();
    const filteredCalcAreas = calcAreas.filter(ca => ca.name !== "OVERVIEW");

    const isCalcInProgress = entry?.calcSetting?.calcAreas?.some(calcArea =>
        calcArea.calculations?.some(calculation => !calculation.resultAvailable)
    ) || false;

    const overviewAreaAndCalculation = useMemo(() => calcAreas
        .filter(calcArea => calcArea.calculations !== undefined)
        .flatMap(calcArea => calcArea.calculations.map(calculation => ({ calcArea, calculation })))
        .filter(areaAndCalculation => areaAndCalculation.calculation.calculationType === "OVERVIEW")
        .filter(areaAndCalculation => areaAndCalculation.calculation.overviewResult)
        .sort((a, b) => (b.calculation.id ?? 0) - (a.calculation.id ?? 0))[0]
    , [calcAreas,isCalcInProgress]);

    const baseHeight = useMemo(() => 0, [overviewAreaAndCalculation]);

    const {
        cameraSettings,
        outdoorResultVisibleInput,
        indoorResultVisibleInput,
        planVisibleInput,
        attenuationGlobalInputActive,
        attenuationGlobalInput,
        latitudeInput,
        longitudeInput,
        buildingsElevationInput,
        radiusInput,
        buildingsVisibleInput,
        buildingsVisibleInputEnabled,
        buildingsTranslationXInput,
        buildingsTranslationYInput,
        buildingsTranslationZInput,
        buildingsObject,
        triangles,
        initSetVisibility,
        setVisibility,
        onAreaItemHighlight,
        onAreaItemHighlightEnd,
        isAreaHighlighted,
        onCameraViewChanged,
        setBuildings,
        setBuildingsTranslation,
        setPlans,
    } = useOverview3dDisplaySettings(layerName);

    const {
        movementActive,
        toggleMovementActive,
    } = useOverview3dBuildingMovement(
        buildingsTranslationXInput,
        buildingsTranslationYInput,
        buildingsTranslationZInput,
    );

    const {
        isPointUnderTriangleArray,
        processPoint,
        pointsPositions,
        upscaledFieldArray,
        upscaledNumberU,
        upscaledNumberV,
        upscaledNumberW,
        preprocessingActive
    } = useOverview3dDisplayDataPreprocessing(
        triangles,
        fieldArray,
        overviewSetting,
        overviewAreaAndCalculation?.calculation?.overviewResult,
        overviewAreaAndCalculation?.calcArea,
    );

    const getResultColor = index => {
        const isPointUnderAnyTriangle = isPointUnderTriangleArray[index];
        return pointsPositions[index].z <= 2.3 || isPointUnderAnyTriangle ? "#ff0000" : "#038307";
    };

    const showResultPoint = index => {
        const value = upscaledFieldArray[index];
        if (!processPoint[index]) {
            return false;
        }
        const pointUnderAnyTriangle = isPointUnderTriangleArray[index];
        // Apply 10dB dumping in internal space
        if (attenuationGlobalInput.value
            && pointUnderAnyTriangle
            && value * 0.1 < overviewSetting?.fieldLimit) {
            return false;
        }
        const isPointCritical = pointsPositions[index].z <= 2.3 || pointUnderAnyTriangle;
        return !isPointCritical && outdoorResultVisibleInput.value
            || isPointCritical && indoorResultVisibleInput.value;
    };

    const addResult = useDebounce(
        plot3d => {
            if (overviewAreaAndCalculation) {
                const { calcArea, calculation } = overviewAreaAndCalculation;
                if (resultObj.current) {
                    plot3d.scene.remove(resultObj.current);
                }
                if (calcArea && calculation && upscaledFieldArray && (outdoorResultVisibleInput.value || indoorResultVisibleInput.value) && isPointUnderTriangleArray) {
                    resultObj.current = plot3d.addResult(calcArea, calculation.overviewResult.sliceDist, upscaledNumberU, upscaledNumberV, upscaledNumberW, getResultColor, showResultPoint);
                }
            }
        },
        300
    );

    const dowloadResult = async plot3d => {
        if (!overviewAreaAndCalculation) {
            return;
        }
        try {
            setResultLoaded(false);
            const { calculation } = overviewAreaAndCalculation;
            const fieldArrayStr = await overviewRest.getFieldArray(calculation.id);
            setFieldArray(fieldArrayStr.split("\n").map(num => Number.parseFloat(num)));
            addResult(plot3d);
        } finally {
            setResultLoaded(true);
        }
    };

    const dowloadAntennas = async plot3d => {
        const antennasNames = entry.sectors.map(sector => sector.antennaName);
        const antennasNamesUnique = [...new Set(antennasNames)];
        let antennas = [];
        if (antennasNamesUnique.length !== 0) {
            antennas = await antennaRest.getAntennasByNames(antennasNamesUnique);
        }
        plot3d.addAntennas(entry.sectors, antennas);
    };

    const addCalcAreas = plot3d => {
        initSetVisibility(plot3d, filteredCalcAreas, baseHeight);
    };

    const loadBuildings = async plot3d => {
        if (buildingsVisibleInput.value) {
            const originHeight = buildingsElevationInput.value;
            const buildings = await geoRest.getBuildings(Number(longitudeInput.value), Number(latitudeInput.value), radiusInput.value);
            const correctedHeightBuildings = buildings
                .map(building => building
                    .map(geometry => ({
                        ...geometry,
                        points: geometry.points.map(point => ({ ...point, z: point.z - originHeight })),
                    })));
            setBuildings(plot3d, correctedHeightBuildings, buildingColor);
        } else {
            setBuildings(plot3d, undefined, buildingColor);
        }
    };

    const loadPlans = async plot3d => {
        if (planVisibleInput.value) {
            const { plans } = entry;
            const planImages = await Promise.all(
                plans
                    .filter(p => p.id !== undefined && p.id !== null)
                    .map(plan => planRest.getImageById(plan.id))
            );
            setPlans(plot3d, plans, planImages, baseHeight);
        } else {
            setPlans(plot3d, undefined, undefined);
        }
    };

    const resetView = async () => {
        await plot3d.controls.setLookAt(0, 0, 80, 0, 0, 0);
        await plot3d.controls.zoomTo(1, false);
    };

    const toNamedAreas = areas => {
        const usedNames = [];
        return areas.map(area => {
            const name = calcAreaUtils.nextAreaName(calcAreas, usedNames);
            usedNames.push(name);
            return { ...area, name };
        });
    };

    const importAreas = async () => {
        try {
            setAreaImportRunning(true);
            if (buildingsVisibleInputEnabled) {
                const buildings = await geoRest.getBuildings(Number(longitudeInput.value), Number(latitudeInput.value), radiusInput.value);
                const areas = buildings.map(building => lod2.convertBuildingPointsToHorizontalArea(building));
                const translationX = buildingsTranslationXInput.value ?? 0;
                const translationY = buildingsTranslationYInput.value ?? 0;
                const translationZ = buildingsTranslationZInput.value ?? 0;
                const buildingsElevation = buildingsElevationInput.value ?? 0;
                const correctedAreas = areas
                    .map(area => ({
                        ...area,
                        originX: area.originX + translationX,
                        originY: area.originY + translationY,
                        originZ: area.originZ + translationZ - buildingsElevation,
                        boundary: area.boundary.map(path => path.map(p => ({x: p.x + translationX, y: p.y + translationY}))),
                    }));
                const namedAreas = toNamedAreas(correctedAreas);
                calcSetting.calcAreas = [...calcAreas, ...namedAreas];
                entryContext.setEntry(entry);
                for (const calcArea of calcSetting.calcAreas) {
                    if (!calcArea.id) {
                        setVisibility(plot3d, calcArea, baseHeight, true);
                    }
                }
            }
        } finally {
            setAreaImportRunning(false);
        }
    };

    useEffect(() => {
        let plot3d;
        if(!isCalcInProgress) {
            plot3d = new Plot3d({ cameraSettings, onCameraViewChanged });
            plot3d.changeCameraLookAt(0, 0, -1);
            dowloadResult(plot3d);
            dowloadAntennas(plot3d);
            addCalcAreas(plot3d);
            setPlot3d(plot3d);
            loadBuildings(plot3d);
            loadPlans(plot3d);
        }

        return () => {
            plot3d?.dispose();
            document.body.style.cursor = "default";
        };
    }, [isCalcInProgress]);

    useEffect(() => {
        if (resultLoaded && !preprocessingActive) {
            document.body.style.cursor = "default";
        } else {
            document.body.style.cursor = "progress";
        }
    }, [resultLoaded, preprocessingActive]);

    useEffect(() => addResult(plot3d), [
        isPointUnderTriangleArray,
        outdoorResultVisibleInput.value,
        indoorResultVisibleInput.value,
        attenuationGlobalInput.value,
    ]);

    useOnChange(() => {
        loadBuildings(plot3d);
    }, [buildingsVisibleInput]);

    useOnChange(() => {
        setBuildingsTranslation(buildingsObject);
    }, [buildingsTranslationXInput, buildingsTranslationYInput, buildingsTranslationZInput]);

    useOnChange(() => {
        loadPlans(plot3d);
    }, [planVisibleInput]);

    return (
        <LayerStyle $show={show}>
            <Grid columns="1">
                <Button
                    disabled={!buildingsVisibleInputEnabled || areaImportRunning}
                    onClick={importAreas}
                    value={intl.get("entry.plan.overview3d.importAreas")} />
                <Button
                    onClick={resetView}
                    value={intl.get("entry.plan.overview3d.resetView")} />
            </Grid>
            <Spacer variant="horizontal" />
            <Line />
            <Spacer variant="horizontal" />
            <Grid columns="2">
                <Checkbox {...outdoorResultVisibleInput.bind}
                    title={intl.get("entry.plan.overview3d.outdoorResultVisible")} />
                <Checkbox {...indoorResultVisibleInput.bind}
                    title={intl.get("entry.plan.overview3d.indoorResultVisible")} />
                <Checkbox {...planVisibleInput.bind}
                    title={intl.get("entry.plan.overview3d.planVisible")} />
                <Checkbox {...attenuationGlobalInput.bind}
                    disabled={!attenuationGlobalInputActive}
                    title={intl.get("entry.plan.overview3d.attenuationGlobal")} />
            </Grid>
            <Spacer variant="horizontal" />
            <Line />
            <Spacer variant="horizontal" />
            <Grid columns="2">
                <InputLatLng
                    {...latitudeInput.bind}
                    title={intl.get("entry.plan.overview3d.latitude")}
                    type="lat" />
                <InputLatLng
                    {...longitudeInput.bind}
                    title={intl.get("entry.plan.overview3d.longitude")}
                    type="lng" />
            </Grid>
            <Spacer variant="horizontal" />
            <Grid columns="3">
                <InputNumber {...buildingsElevationInput.bind}
                    title={intl.get("entry.plan.overview3d.buildingElevation")} />
                <InputNumber
                    {...radiusInput.bind}
                    title={intl.get("entry.plan.overview3d.radius")}
                    fractionDigits={0} />
                <Checkbox {...buildingsVisibleInput.bind}
                    disabled={!buildingsVisibleInputEnabled}
                    title={intl.get("entry.plan.overview3d.buildingsVisible")} />
            </Grid>
            <Spacer variant="horizontal" />
            <Grid template="1fr 1fr 1fr 0.2fr">
                <InputNumber {...buildingsTranslationXInput.bind} fractionDigits={2}
                    title={intl.get("entry.plan.overview3d.buildingTranslationX")} />
                <InputNumber {...buildingsTranslationYInput.bind} fractionDigits={2}
                    title={intl.get("entry.plan.overview3d.buildingTranslationY")} />
                <InputNumber {...buildingsTranslationZInput.bind} fractionDigits={2}
                    title={intl.get("entry.plan.overview3d.buildingTranslationZ")} />
                <MovementButtonStyle>
                    <Button
                        compact
                        svgIcon={mdiCursorMove}
                        disabled={!(buildingsVisibleInput.value === true)}
                        onClick={() => toggleMovementActive(plot3d)} />
                    <ActiveDotStyle $active={movementActive}/>
                </MovementButtonStyle>
            </Grid>
            <Spacer variant="horizontal" />
            <Line />
            <Spacer variant="horizontal" />
            <Overview3dAntennasList entry={entry} triangles={triangles} />
            <Spacer variant="horizontal" />
            <Line />
            <Spacer variant="horizontal" />
            <ScrollBox>
                {filteredCalcAreas.map((calcArea, index) => (
                    <div
                        key={`${calcArea.id}#${calcArea.name}`}
                        onMouseEnter={() => onAreaItemHighlight(plot3d, calcArea)}
                        onMouseLeave={() => onAreaItemHighlightEnd(plot3d, calcArea)}>
                        <Overview3dAreaItem
                            index={index}
                            calcArea={calcArea}
                            active={false}
                            highlighted={isAreaHighlighted(calcArea)}
                            isVisible={calcArea.overview3dVisible ?? true}
                            onVisibilityChanged={val => setVisibility(plot3d, calcArea, baseHeight, val)} />
                    </div>
                ))}
            </ScrollBox>
        </LayerStyle>
    );
};